import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import { changeSmallNumberFormat } from '../../../shared/helpers';
import { formatHTMLMessageHelper } from '../../../shared/helpers/internationalization';
import { CheckResult } from '../types';
import { Link } from 'react-router-dom';
import { COMPETITIONS } from '../../../shared/constants';

const convertValueToType = (value: number, propName: string, tableInfoTypes: Object[], toFixedValue = 2): string => {
  const proptype: any = tableInfoTypes ? tableInfoTypes.find((property: any) => property.id === propName) : [];
  if (proptype) {
    return changeSmallNumberFormat({ value, type: proptype.type, toFixedValue, isCheck: true });
  } else {
    return `${value}`;
  }
};

interface StatusMessagesProps {
  check: Record<string, any>;
  stage: string;
  tableInfoTypes: Object[];
}

type StatusMessagesPropsGeneric = StatusMessagesProps & InjectedIntlProps;

interface CheckMatch {
  id: string;
  multiplier?: number;
  effective?: number;
  name: string;
}

const formattedCheckTypes: Record<string, string> = {
  LOW_FITNESS: 'fitness',
  LOW_SHARPE: 'sharpe',
  IS_LADDER_SHARPE: 'sharpe',
  HIGH_TURNOVER: 'turnover',
  HT_TURNOVER: 'turnover',
  HT_INVESTABLE_MAX_TRADE_TURNOVER: 'turnover',
  HT_INVESTABLE_MAX_POSITION_TURNOVER: 'turnover',
  LOW_TURNOVER: 'turnover',
  MATCHES_THEMES: 'themeMatch',
  MATCHES_PYRAMID: 'themeMatch',
  MATCHES_COMPETITION: 'competitionMatch',
  LOW_ROBUST_UNIVERSE_SHARPE: 'sharpe',
  LOW_ROBUST_UNIVERSE_RETURNS: 'returns',
  LOW_RETURNS: 'returns',
  IMBALANCE: 'turnover',
  CONCENTRATED_WEIGHT: 'turnover',
};

function formatThemeMatches(matches: CheckMatch[], check: Record<string, any>) {
  if (typeof matches[0].multiplier === 'number') {
    const formattedMatches = matches.map(match => `${match.name} — ${match.multiplier}`);
    return { matches: formattedMatches.join('; '), finalMultiplier: check.multiplier, effective: check.effective };
  }
  return { matches: matches.map(match => match.name).join(', ') };
}

export function getFormattedMatch(match: CheckMatch) {
  return { ...match, multiplier: match.multiplier?.toString(10) };
}

const MULTIPLE_VALUE_CHECKS_NAMES = ['MATCHES_THEMES', 'MATCHES_COMPETITION', 'MATCHES_PYRAMID', 'MATCHES_CLASSIFICATION'] as const;
type MultipleValueChecks = typeof MULTIPLE_VALUE_CHECKS_NAMES[number];
const MULTIPLE_VALUE_CHECKS = new Set(MULTIPLE_VALUE_CHECKS_NAMES);

function getMatches(name: MultipleValueChecks, check: Record<string, any>, result: 'PASS' | 'WARNING' | 'PENDING') {
  if (name === 'MATCHES_THEMES' && check.themes) {
    return check.themes;
  }

  if (name === 'MATCHES_COMPETITION' && check.competitions) {
    return check.competitions;
  }

  if (name === 'MATCHES_PYRAMID' && check.pyramids) {
    return check.pyramids;
  }

  if (name === 'MATCHES_CLASSIFICATION' && check.value) {
    return check.value;
  }

  // In the future, MATCHES_THEMES should be along with `themes`,
  // and MATCHES_COMPETITION should be along with `competitions` as above
  // This is for backward compatible with old schema
  // https://jira.worldquant.com/browse/BRAINTECH-2660
  // TODO: Remove this when backend got updated to new schema
  return result === 'PASS' ? check.matched : check.unmatched;
}

export const CheckMessage = injectIntl(({ check, stage, tableInfoTypes, intl }: StatusMessagesPropsGeneric) => {
  check = Object.assign({}, check);

  const name = check.name;
  const result: CheckResult = check.result;
  const statusType = stage === 'IS' ? 'statusMessageIS' : 'statusMessageOS';

  if ('ratio' in check) {
    check.ratio = changeSmallNumberFormat({ value: check.ratio, type: 'percent', isCheck: true });
  }

  if (formattedCheckTypes.hasOwnProperty(name)) {
    for (const property of ['value', 'limit', 'max', 'min']) {
      if (check[property] !== undefined) {
        check[property] = convertValueToType(check[property], formattedCheckTypes[name], tableInfoTypes);
      }
    }
  }
  for (const property of ['startDate', 'endDate', 'date']) {
    if (check[property] !== undefined) {
      check[property] = new Date(check[property]);
    }
  }

  if (name === 'LOW_ROBUST_UNIVERSE_SHARPE' && check.ratio != null) {
    return <div>{formatHTMLMessageHelper(intl, `${statusType}.${name}.WITH_RATIO.${result}`, check)}</div>;
  }

  if (check.value === undefined) {
    // SELF_CORRELATION and PROD_CORRELATION with no correlated alphas have no correlation 'value'. Output 'none' in the message.
    check = Object.assign({}, check);
    check.value = 'none';
  }

  if (MULTIPLE_VALUE_CHECKS.has(name)) {
    if (
      result === 'PASS' ||
      result === 'WARNING' ||
      // Only MATCHES_THEMES and MATCHES_COMPETITION has result PENDING with list of pending themes/ competitions
      (name === 'MATCHES_THEMES' && result === 'PENDING' && check.themes) ||
      (name === 'MATCHES_COMPETITION' && result === 'PENDING' && check.competitions)
    ) {
      const matches: CheckMatch[] = getMatches(name, check, result);

      if (matches.length === 1) {
        const firstMatch = matches[0];
        if (name === 'MATCHES_COMPETITION' && firstMatch.id) {
          return (
            <div>
              {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.single.beginning`)}
              <CompetitionLink match={firstMatch} />
              {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.single.end`)}
            </div>
          );
        }
        const formattedMatch = { ...check, ...getFormattedMatch(firstMatch) };

        return <div>{formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.single`, formattedMatch)}</div>;
      } else if (matches.length > 1) {
        if (name === 'MATCHES_COMPETITION' && matches[0].id) {
          return (
            <div>
              {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.multiple.beginning`)}
              {matches.map((match, index) => (
                <>
                  {index ? ', ' : ''}
                  <CompetitionLink match={match} />
                </>
              ))}
              {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.multiple.end`)}
            </div>
          );
        } else if (name === 'MATCHES_CLASSIFICATION') {
          return <div>{formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.multiple`, { value: check.value.join('; ') })}</div>;
        }
        return <div>{formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}.multiple`, formatThemeMatches(matches, check))}</div>;
      }
    }
  }

  //concentrated weight can be with or without values
  let withValuesSuffix = '';
  if (name === 'CONCENTRATED_WEIGHT' && ((check.value && check.value !== 'none') || check.limit)) {
    withValuesSuffix = '.WITH_VALUES';
  }

  if (name === 'DATA_DIVERSITY' && (result === 'WARNING' || result === 'FAIL')) {
    const fieldString = check.fields.map((field: string) => `"${field}"`).join(', ');
    return (
      <div>
        {formatHTMLMessageHelper(intl, `${statusType}.DATA_DIVERSITY.NOT_PASSED`, {
          dataCategoryName: check.dataCategoryName,
          field: fieldString,
        })}
      </div>
    );
  }

  if (name === 'DATA_SET_AUTHORIZATION' && result === 'FAIL') {
    return (
      <div>
        {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}`, {
          datasets: check.datasets.join(', '),
        })}
      </div>
    );
  }

  if (name === 'OPERATOR_AUTHORIZATION' && result === 'FAIL') {
    return (
      <div>
        {formatHTMLMessageHelper(intl, `${statusType}.${name}.${result}`, {
          operators: check.operators.join(', '),
        })}
      </div>
    );
  }

  return <div>{formatHTMLMessageHelper(intl, `${statusType}.${name}${withValuesSuffix}.${result}`, check)}</div>;
});

interface CompetitionLinkParams {
  match: CheckMatch;
}

function CompetitionLink({ match }: CompetitionLinkParams) {
  return (
    <Link key={match.id} to={`${COMPETITIONS}/${match.id}`}>
      {match.name}
    </Link>
  );
}
